import { getFromDataPath, mutateByPath, reduceDataPath } from "../../../utility";
import { ArsTableGroup } from "../interface";
import { isArsTableSubGroup } from "../typeguards/isArsTableSubGroup";

/**
 * Inserts table groups at a resolved insertion path.
 *
 * @param initialTableGroups - Existing root table groups (`rows` or `columns`)
 * @param newTableGroups - Table groups to insert
 * @param insertDataPath - Relative insertion path within `initialTableGroups` (for example `/0` or `/0/subGroups/2`)
 * @param isT - Label type guard
 * @returns Updated root table groups
 */
export const arst_insertTableGroups = <T>(
  initialTableGroups: ArsTableGroup<T>[],
  newTableGroups: ArsTableGroup<T>[],
  insertDataPath: string,
  isT: (v: unknown) => v is T,
): ArsTableGroup<T>[] => {
  const pathParts = insertDataPath.split("/").filter(Boolean);
  if (pathParts.length === 1) {
    const rootInsertIndex = parseInt(pathParts[0], 10);
    if (Number.isNaN(rootInsertIndex)) return initialTableGroups;
    const nextRootGroups = [...initialTableGroups];
    nextRootGroups.splice(rootInsertIndex, 0, ...newTableGroups);
    return nextRootGroups;
  }

  if (pathParts.length > 2 && pathParts[pathParts.length - 2] === "subGroups") {
    const parentPath = reduceDataPath(insertDataPath, 2);
    const parentGroup = getFromDataPath(parentPath, initialTableGroups, (obj) =>
      isArsTableSubGroup(obj, isT),
    );
    if (!parentGroup) return initialTableGroups;

    const insertAt = parseInt(pathParts[pathParts.length - 1], 10);
    if (Number.isNaN(insertAt)) return initialTableGroups;
    const nextSubGroups = [...(parentGroup.subGroups ?? [])];
    nextSubGroups.splice(insertAt, 0, ...newTableGroups);
    return mutateByPath(initialTableGroups, `${parentPath}/subGroups`, nextSubGroups);
  }

  return initialTableGroups;
};
